import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, Input, NO_ERRORS_SCHEMA, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  DialogCloseResult, DialogModule, DialogRef, DialogService, KENDO_DIALOGS,
  KENDO_WINDOW, WindowCloseResult, WindowModule, WindowRef, WindowService
} from '@progress/kendo-angular-dialog';
import { DropDownsModule, KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { IconsModule } from '@progress/kendo-angular-icons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { KENDO_LABEL, LabelModule } from '@progress/kendo-angular-label';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import {
  CasiOwnerUser,
  CasiProjectLabel,
  CasiStoryEstimate, CasiStoryPriority,
  CasiStoryStatus, CasiStoryType, CasiUserStory, DashboardStrings, Project,
  User as UserModel
} from '../../models/models.lib';
import { LoggerService } from '../../shared/logger.service';
import { UserStoryService } from '../user-story.service';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { AppEnities, blastKeys, CasiCloseOptions, defaultGridPageSize, FormModes, ItemModes } from '../../common/constants.lib';
import { CompositeFilterDescriptor, SortDescriptor, distinct, filterBy, orderBy, } from '@progress/kendo-data-query';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { GridModule, KENDO_GRID, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { DeleteConfirmComponent } from '../../common-components/delete-confirm/delete-confirm.component';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { KENDO_TOOLBAR } from '@progress/kendo-angular-toolbar';
import { UserStorySearchOptions } from '../../common/obj.lib';
import { UserStoryAddComponent } from '../../user-stories/user-story-add/user-story-add.component';
import { setTitle } from '../../common/utils.lib';
import { UserStoryComponent } from '../../user-stories/user-story/user-story.component';
import { ProjectUserService } from '../project-user.service';
import { formatText } from '../../common/utils.lib';
import { SocketService } from '../../shared/socket.service';
import { HistoryComponent } from '../../user-stories/history/history.component';

const { TRACKER_PROJECT } = AppEnities;

@Component({
  selector: 'casi-project-grid',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    ButtonsModule,
    IndicatorsModule,
    RouterModule,
    IconsModule,
    NavigationModule,
    FormsModule,
    ReactiveFormsModule,
    DropDownsModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    DialogModule,
    WindowModule,
    LabelModule,
    ListViewModule,
    InputsModule,
    MulticheckFilterComponent,
    GridModule,
    KENDO_WINDOW,
    KENDO_DIALOGS,
    KENDO_GRID,
    KENDO_TOOLBAR,
    KENDO_DROPDOWNS,
    KENDO_LABEL,
    DeleteConfirmComponent,
    ErrorDialogModalComponent,
    UserStoryAddComponent,
    UserStoryComponent,
    HistoryComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './project-grid.component.html',
  styleUrl: './project-grid.component.scss'
})
export class ProjectGridComponent implements OnInit, OnDestroy, AfterContentInit, OnChanges {

  private _users: UserModel[] = [];
  @Input({ required: true }) set users(value: UserModel[]) {
    // this.logger.debug(`set users: `, value);
    this._users = value;
  }
  get users() {
    return this._users;
  }

  private _userStories = new BehaviorSubject<CasiUserStory[]>([]);
  get $userStories() {
    return this._userStories.asObservable();
  }
  set userStories(val: CasiUserStory[]) {
    this._userStories.next(val);
  }
  get userStories() {
    return this._userStories.getValue();
  }

  private _selectedProject = new BehaviorSubject<Project | undefined>(undefined);
  get $selectedProject() {
    return this._selectedProject.asObservable();
  }
  @Input({ required: true }) set selectedProject(val: Project | undefined) {
    this._selectedProject.next(val);
  }
  get selectedProject(): Project | undefined {
    return this._selectedProject.getValue();
  }

  get hasSelectedProject(): boolean {
    if (this.selectedProject) {
      return true;
    }
    return false;
  }

  @Input({ required: true }) public dashboard: DashboardStrings = DashboardStrings.CURRENT;

  @Input({ required: true }) public storyStatus: CasiStoryStatus[] = [];
  @Input({ required: true }) public storyTypes: CasiStoryType[] = [];
  @Input({ required: true }) public storyPriorities: CasiStoryPriority[] = [];
  @Input({ required: true }) public storyEstimates: CasiStoryEstimate[] = [];
  @Input({ required: true }) public projectLabels: CasiProjectLabel[] = [];

  private _searchOptions: UserStorySearchOptions = new UserStorySearchOptions({});
  @Input({ required: true }) set searchOptions(val: UserStorySearchOptions) {
    this._searchOptions = val || new UserStorySearchOptions({});
  }
  get searchOptions() {
    return this._searchOptions;
  };

  public formatText = formatText;
  public formMode: FormModes = FormModes.NOTSET;
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };

  public gridData: CasiUserStory[] = filterBy(this.userStories, this.filter);

  public dashboardEnums = DashboardStrings;

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = [5, 10, 25, 50, 100];
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = defaultGridPageSize;
  public skip = 0;
  public buttonCount = 5;

  public canUpdate: boolean = true;
  public canCreate: boolean = true;
  public canDestroy: boolean = true;

  public editWinOpen: boolean = false;

  public projectUsers: CasiOwnerUser[] = [];
  public currentItem: CasiUserStory | undefined;
  public currentUser: UserModel | undefined;
  public selectedUserStory: CasiUserStory = this.gridData[0];
  public selectedUserStoryId = null;
  public vTop: any = { 'vertical-align': 'top' };

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  public sort: SortDescriptor[] = [
    {
      field: "id",
      dir: "desc",
    },
  ];

  private _subs: Subscription[] = [];

  private _windowRef: WindowRef | undefined;
  private _dialogRef: DialogRef | undefined;

  constructor(
    private logger: LoggerService,
    public faIcons: FaIconLibrary,
    public dataService: UserStoryService,
    private me: MeService,
    private toastr: ToastrService,
    private dialogService: DialogService,
    private userStoryService: UserStoryService,
    private windowService: WindowService,
    private projectUserService: ProjectUserService,
    private viewContainerRef: ViewContainerRef,
    private socketService: SocketService
  ) {
    faIcons.addIcons(...all);
  }

  //#region Ng Event Handlers

  ngOnChanges(changes: SimpleChanges): void {
    // this.logger.debug('ngOnChanges...', changes);
    const { selectedProject, searchOptions } = changes;
    // this.logger.debug('ngOnChanges:selectedProject', selectedProject);
    // this.logger.debug('ngOnChanges:searchOptions', searchOptions);
    if (searchOptions) {
      this.searchOptions = searchOptions.currentValue;
      const { currentValue } = searchOptions;
      if (currentValue) {
        if (!!currentValue.projectId) {
          this.doDataRequest();
        }
      }
    }
  }

  ngAfterContentInit(): void {
    // this.logger.debug('ngAfterContentInit...');
  }

  ngOnDestroy(): void {
    // this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  ngOnInit(): void {
    // this.logger.debug('ngOnInit...', this.constructor.name);
    // const { can_Create, can_Update, can_Destroy } = this.me.allICanDo(TRACKER_PROJECT)
    this.canUpdate = true; // can_Update;
    this.canCreate = true; // can_Create;
    this.canDestroy = true; // can_Destroy;
    let found = this.users.find((value, index, array) => {
      return value.id == this.me.userId;
    });
    if (found) {
      this.currentUser = found;
      // this.logger.debug('ngOnInit:currentUser:', this.currentUser);
    }

    this._subs.push(this.socketService.onRefresh()
      .subscribe((args: any | unknown) => {
        this.logger.debug(`onRefresh: `, args);
        if (args && args.blastKey === blastKeys.USER_STORY_UPDATED) {
          this.doDataRequest();
        }
      }));
    // this.logger.debug('ngOnInit:permissions: ', this.canCreate, can_Create);
  }

  //#endregion

  //#region View

  public close(): void {
    // this.logger.debug('close...');
    this.editWinOpen = false;
  }

  public onAddClicked(titleBar: TemplateRef<unknown>): void {
    // this.logger.debug("onAddClicked...");
    this.closeWindowRef();
    this.formMode = FormModes.NEW;
    // this.logger.debug("showAddWindow:currentUser ", this.currentUser);
    this.showTitle = setTitle(this.formMode, 'USER STORY');
    let tmpUserStory: CasiUserStory | undefined; // = new CasiUserStory(newStory);

    this.userStoryService.add(CasiUserStory.createWithDefaults(this.currentUser, this.selectedProject,
      this.storyStatus, this.storyTypes,
      this.storyPriorities, this.storyEstimates
    ))
      .then(newStory => {
        newStory.itemMode = ItemModes.CREATION;
        tmpUserStory = new CasiUserStory(newStory);
        this._windowRef = this.windowService.open({
          titleBarContent: titleBar,
          content: UserStoryAddComponent,
          width: 1000,
          minWidth: 320,
          draggable: true
        });
        this.setAddInstanceInputs(tmpUserStory);
        this._windowRef.content.instance.submitForm.subscribe((formData: FormGroup) => {
          // this.logger.debug('showAddWindow:formData: ', formData);
          tmpUserStory = undefined;
          this.handleWinRefClose(formData.value, this.formMode);
        });

        this._windowRef.result.subscribe(results => {
          // this.logger.debug('showAddWindow:results: ', results, tmpUserStory);
          if (results instanceof WindowCloseResult) {
            this.closeWindowRef(tmpUserStory);
          }
        });

        this._windowRef.content.instance.closeForm.subscribe((formData: FormGroup) => {
          // this.logger.debug('showAddWindow:closeForm: formData: ', formData);
          this.closeWindowRef(tmpUserStory);
        });

        this.editWinOpen = true;

      })
      .catch(err => {
        this.logger.error(`showAddWindow:new user error: `, err);
      })
  }

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    // this.logger.debug("onEditClick: ", dataItem);
    if (dataItem) {
      this.closeWindowRef();
      this.formMode = FormModes.EDIT;
      // this.logger.debug("onEditClick:currentUser ", this.currentUser);
      this.showTitle = setTitle(this.formMode, 'USER STORY');
      this.selectedUserStory = dataItem;
      this.selectedUserStoryId = dataItem.id ? dataItem.id : undefined;
      this.projectUserService.getByProjectId(dataItem.project_id)
        .then((data: any) => {
          this.projectUsers = data.map((r: any) => CasiOwnerUser.createFromProjectUser(r));
          // this.logger.debug('onEditClick:projectUsers:', this.projectUsers);
          this._windowRef = this.windowService.open({
            titleBarContent: titleBar,
            content: UserStoryComponent,
            width: 1000,
            minWidth: 320
          });
          this.setEditInstanceInputs(dataItem);
          this._windowRef.content.instance.submitForm.subscribe((formData: FormGroup) => {
            // this.logger.debug('onEditClick:formData: ', formData);
            this.handleEditWinRefClose(formData.value, this.formMode);
          });

          this._windowRef.result.subscribe(results => {
            // this.logger.debug('onEditClick:results: ', results);
            if (results instanceof WindowCloseResult) {
              this.closeEditWindowRef();
            }
          });

          this._windowRef.content.instance.closeForm.subscribe((formData: FormGroup) => {
            // this.logger.debug('onEditClick:closeForm: formData: ', formData);
            this.closeEditWindowRef();
          });

          this.formMode = FormModes.EDIT;
          // this.showTitle = setTitle(this.formMode, 'PROJECT');
          this.editWinOpen = true;
        }).catch((error: any) => {
          this.logger.error(`onEditClick:error:`, error);
        });
    }
  }

  onViewHistoryClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    // this.logger.debug("onViewHistoryClick: ", dataItem);
    if (dataItem) {
      this.closeWindowRef();
      this.formMode = FormModes.VIEW;
      // this.logger.debug("onViewHistoryClick:currentUser ", this.currentUser);
      this.showTitle = setTitle(this.formMode, 'USER STORY HISTORY');
      this.selectedUserStory = dataItem;
      this.selectedUserStoryId = dataItem.id ? dataItem.id : undefined;
      this._windowRef = this.windowService.open({
        titleBarContent: titleBar,
        content: HistoryComponent,
        width: 1500,
        minWidth: 320
      });
      this.setEditInstanceInputs(dataItem);

      this._windowRef.result.subscribe(results => {
        // this.logger.debug('onViewHistoryClick:results: ', results);
        if (results instanceof WindowCloseResult) {
          this.closeEditWindowRef();
        }
      });
    }
  }

  onConfirmDelete(dataItem: any): void {
    // this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = dataItem;
    this.openConfirmDailog(this.currentItem);
  }

  public openConfirmDailog(dataItem: any): void {
    // this.logger.info("openConfirmDailog: ", dataItem);
    this.currentItem = dataItem;
    this._dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 320,
      width: 750,
      appendTo: this.viewContainerRef
      // cssClass: "custom-css-class"
    });

    let contentRef = this._dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete User Story - <b>${this.currentItem?.id}</b>?`;

    this._dialogRef.result.subscribe((r: any) => {
      // this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        this.closeDialogRef();
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        // this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentItem);
        this.handleRemove();
        return;
      }
      if (r.text === CasiCloseOptions.Closed) {
        this.closeDialogRef();
      }
    });
  }

  public copyStoryID(id: number): void {
    navigator.clipboard.writeText(id.toString());
    this.toastr.success(`Story ID ${id} copied to clipboard`);
  }

  public copyLink(us: CasiUserStory): void {
    // this.logger.debug('us', us);
    let url = `${window.location.host}/userstory/show/${us.id}`;
    // this.logger.debug('copy url', url);
    navigator.clipboard.writeText(url);
    this.toastr.success(`URL ${url} copied to clipboard`);
  }

  move(dataItem: any, direction: string): void {
    this.logger.debug("move: ", {dataItem, direction});
    if (dataItem) {
      this.userStoryService.move(dataItem.id, direction == 'up' ? dataItem.previous_id : dataItem.next_id)
        .then(() => this.doDataRequest())
        .catch((error: any) => {
          this.logger.error(`move:error:`, error);
        });
    }
  }

  //#endregion

  //#region Grid handlers

  public pageChange({ skip, take }: PageChangeEvent): void {
    this.skip = skip;
    this.pageSize = take;
    this.loadItems();
  }

  onClearFilters(gridName: string) {
    // this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadItems();

    // this.fetchUserStories(this.selectedProject);
  }

  public sortChange(sort: SortDescriptor[]): void {
    // this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadItems();
    // this.fetchUserStories(this.selectedProject);
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    // this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadItems();
    // this.fetchUserStories(this.selectedProject);
  }

  public distinctPrimitive(fieldName: string, descending: boolean = false): any[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    //return distinct(this.userStories, fieldName).map((item: any) => item[fieldName]);
    let data = distinct(this.userStories, fieldName).map((item: any) => item[fieldName]);
    if (descending)
      return data.sort().reverse();
    else
      return data.sort();
    // return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region private functions

  private handleRemove(): void {
    if (this.currentItem && this.currentItem.id) {
      let d = this.storyStatus.find((value) => {
        return value.user_story_status_description.toLowerCase() === 'deleted';
      });
      if (d) {
        this.currentItem.user_story_status_id = d.id;
        this.currentItem.user_story_status_description = d.user_story_status_description;
        this.dataService.update(this.currentItem)
          .then((result: any) => {
            this.notify('User Story Deleted');
            // this.doDataRequest();
          }).catch((error: any) => {
            this.notify(`Error Deleting User Story: ${error.error}`, true);
          })
          .finally(() => this.closeDialogRef());
      }
    }
  }

  private loadItems(): void {
    // this.logger.debug(`loadItems: `, { skip: this.skip, pageSize: this.pageSize })
    this.gridData = (this.dashboard !== this.dashboardEnums.ICEBOX && this.dashboard !== this.dashboardEnums.CURRENT)
      ? orderBy(filterBy(this.userStories, this.filter), this.sort) : filterBy(this.userStories, this.filter);

    // this.gridData = filterBy(this.userStories, this.filter);
  }

  private doDataRequest(): void {
    this.dataService.searchForUser(this.searchOptions.searchStr, this.searchOptions)
      .then(data => {
        if (data) {
          this.userStories = data;
          this.loadItems();
        } else {
          this.userStories = [];
          this.loadItems();
        }
      })
      .catch(err => {
        this.logger.error(`doDataRequest:error: `, err);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private handleWinRefClose(formData: any, mode: FormModes): void {
    this.logger.debug('handleWinRefClose...', formData);

    this.userStoryService.update(formData)
      .then(resp => {
        this.notify('User Story Added');
        this.closeWindowRef();
      })
      .catch(err => {
        this.logger.error('handleWinRefClose:', err);
        this.notify('Error saving user story', true);
      });
  }

  private handleEditWinRefClose(formData: any, mode: FormModes): void {
    this.logger.debug('handleEditWinRefClose...', formData);
    this.closeEditWindowRef();
  }

  private setAddInstanceInputs(story: CasiUserStory): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.showProjects = false;
      inputs.projects = [this.selectedProject];
      inputs.storyTypes = this.storyTypes;
      inputs.storyStatuses = this.storyStatus;
      inputs.storyPriorities = this.storyPriorities;
      inputs.storyEstimates = this.storyEstimates;
      inputs.users = this.users;
      inputs.userStory = story;
      inputs.projectLabels = this.projectLabels.filter(pl => pl.active);
      inputs.formMode = this.formMode;
      this.logger.debug('setAddInstanceInputs: ', inputs);
    }
  }

  private setEditInstanceInputs(story: CasiUserStory): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.selectedUserStoryId = this.selectedUserStoryId;
      inputs.showProjects = false;
      // inputs.userStory = this.selectedUserStory;
      inputs.projects = [this.selectedProject];
      inputs.storyTypes = this.storyTypes;
      inputs.storyStatuses = this.storyStatus;
      inputs.storyPriorities = this.storyPriorities;
      inputs.storyEstimates = this.storyEstimates;
      inputs.users = this.users;
      inputs.userStory = story;
      inputs.projectLabels = this.projectLabels;
      inputs.modUsers = this.projectUsers;
      this.logger.debug('setEditInstanceInputs: ', inputs);
    }
  }

  private closeWindowRef(tmp?: CasiUserStory): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
    if (tmp) {
      this.logger.debug('closeWindowRef: tmp: ', tmp);
      this.userStoryService.destroy(tmp.id!);
    }
  }

  private closeEditWindowRef(tmp?: CasiUserStory): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
    this.doDataRequest();
  }

  private closeDialogRef(): void {
    this.logger.info(`closeDialogRef...`)
    if (this._dialogRef) {
      this._dialogRef.close();
      this._dialogRef = undefined;
    }
  }

  //#endregion

}
